import axios from "axios";
import React, { useEffect, useState } from "react";
import StickyTable from "../../components/common/StickyTable";
import {
  MEMBER_MONTHLY_REPORTS_LIST,
  MEMBER_MONTHLY_REPORTS_GENERATE_MONTH,
} from "../../routes/routes";
import { PAGINATE } from "../../constant/constant";
import { NoDataFound, Pagination } from "../../components/Utils";
import Loader from "../../components/common/Loader";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const MemberMonthlyReportsList = () => {
  const [data, setData] = useState([]);
  const [paginator, setPaginator] = useState({});
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [generatingMonth, setGeneratingMonth] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        const response = await axios.get(MEMBER_MONTHLY_REPORTS_LIST, {
          params: {
            type: PAGINATE,
            page,
            perPage,
          },
        });
        if (response.status === 200 && response.data.success) {
          setData(response.data.data.reports || []);
          setPaginator({
            prev: response.data.data.prev != null,
            next: response.data.data.next != null,
            total: response.data.data.total,
            current: response.data.data.current_page,
            last: response.data.data.last_page,
          });
        }
      } catch (error) {
        toast.error(error?.response?.data?.message || error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [page, perPage, refresh]);

  const handleGenerateSelectedMonth = async () => {
    if (!selectedMonth) {
      toast.error("Please select a month and year");
      return;
    }

    try {
      setGeneratingMonth(true);
      const month = moment(selectedMonth).format("YYYY-MM");
      const response = await axios.get(MEMBER_MONTHLY_REPORTS_GENERATE_MONTH, {
        params: { month },
      });
      if (response.status === 200 && response.data.success) {
        toast.success(response.data.message || "Report generated successfully");
        setRefresh((prev) => !prev);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message);
    } finally {
      setGeneratingMonth(false);
    }
  };

  return (
    <>
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <h5 className="page-title">Member Monthly Reports</h5>
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
              <ol className="breadcrumb breadcrumb-style1 mb-0">
                <li>
                  <div className="d-flex align-items-center gap-2">
                    <div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="50"
                        height="50"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#212b39"
                        strokeWidth="1"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                        <polyline points="9 22 9 12 15 12 15 22"></polyline>
                      </svg>
                    </div>
                    <div>
                      <Link to={"/dashboard"}>
                        <span>Dashboard</span>
                      </Link>
                    </div>
                  </div>
                </li>
                <li>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="50"
                    height="50"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#212b39"
                    strokeWidth="1"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </li>
                <li>
                  <span className="active_breadcrumb">
                    Member Monthly Reports
                  </span>
                </li>
              </ol>
              <div className="d-flex flex-nowrap align-items-center gap-2 member-monthly-report-actions">
                <div className="datepicker_wrapper member-monthly-month-picker">
                  <div className="datepicker">
                    <DatePicker
                      portalId="root-portal"
                      wrapperClassName="w-100 d-block"
                      className="form-control"
                      selected={selectedMonth}
                      onChange={(date) => setSelectedMonth(date)}
                      dateFormat="MMMM yyyy"
                      showMonthYearPicker
                      placeholderText="Select Month"
                      maxDate={moment().toDate()}
                    />
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-primary text-nowrap"
                  disabled={generatingMonth || !selectedMonth}
                  onClick={handleGenerateSelectedMonth}
                >
                  {generatingMonth ? "Generating..." : "Generate Report"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-grow-1">
        <div className="card icard sticky-table-card">
          <div className="container-fluid f_containter">
            {data.length > 0 && (
              <Pagination
                paginator={paginator}
                page={(no) => setPage(no)}
                perPage={perPage}
                setPerPage={setPerPage}
              />
            )}
            <StickyTable refreshDeps={[data, loading]}>
              <table className="table table-striped itable sticky-action-col">
                <thead>
                  <tr>
                    <th>Sl. No.</th>
                    <th>Period</th>
                    <th>Joined Members</th>
                    <th>Renewed Members</th>
                    <th>Generated At</th>
                  </tr>
                </thead>
                <tbody className="table-border-bottom-0">
                  {loading ? (
                    <Loader loading={loading} colspan="5" />
                  ) : data.length > 0 ? (
                    data.map((report) => (
                      <tr key={report.id}>
                        <td>{report.sno}</td>
                        <td>{report.period_label}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span>{report.joined_count}</span>
                            {report.joined_file_url ? (
                              <a
                                href={report.joined_file_url}
                                target="_blank"
                                rel="noreferrer"
                                className="badge bg-label-success border-0"
                                download
                              >
                                Download
                              </a>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span>{report.renewed_count}</span>
                            {report.renewed_file_url ? (
                              <a
                                href={report.renewed_file_url}
                                target="_blank"
                                rel="noreferrer"
                                className="badge bg-label-info border-0"
                                download
                              >
                                Download
                              </a>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </div>
                        </td>
                        <td>{report.generated_at || "—"}</td>
                      </tr>
                    ))
                  ) : (
                    <NoDataFound colspan="5" message="No Data Found" />
                  )}
                </tbody>
              </table>
            </StickyTable>
            {data.length > 0 && (
              <Pagination
                paginator={paginator}
                page={(no) => setPage(no)}
                perPage={perPage}
                setPerPage={setPerPage}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MemberMonthlyReportsList;
